import React, { ChangeEvent, useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons';
import { debounce } from 'lodash';
import { Autocomplete, Box, FilledTextFieldProps, TextFieldProps, PopperProps, Popper, Tooltip, AutocompleteRenderInputParams } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { TextField } from '../../text-field';
import { LookupLoadingIndicator } from './lookup-loading-indicator';

function containsValue<T>(x: T): x is NonNullable<T> {
  return x !== null && x !== undefined && (typeof x !== 'object' || Object.keys(x).length > 0);
}

export interface ILookupAutocompleteProps<DataType, ClassKey extends string = string> {
  classes?: Partial<Record<ClassKey, string>>;
  autoFocus?: TextFieldProps['autoFocus'];
  autoHighlight?: boolean;
  className?: string;
  disableClearable?: true;
  disabled?: boolean;
  error?: boolean;
  errorText?: FilledTextFieldProps['helperText'];
  helperText?: FilledTextFieldProps['helperText'];
  inputElementRootClass?: string;
  inputRootClass?: string;
  label?: string;
  noOptionsText?: React.ReactNode | string;
  onChange?: (value: DataType) => void;
  onSearchValueChange?: (value: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  openOnFocus?: boolean;
  popperComponent?: React.ComponentClass<PopperProps, any> | React.FunctionComponent<PopperProps> | undefined;
  readonly?: boolean;
  required?: boolean;
  size?: TextFieldProps['size'];
  style?: React.CSSProperties;
  value?: DataType;
  variant?: TextFieldProps['variant'];
  throttleMs?: number;
  isLoading?: boolean;
  fetchMinimumInputLength?: number;
  placeholder?: TextFieldProps['placeholder'];
  data?: DataType[];
  isOptionEqualToValue: (option?: DataType, value?: DataType) => boolean;
  getOptionLabel: (option?: DataType) => string;
  renderOption: (props: React.HTMLAttributes<HTMLLIElement>, option?: DataType) => React.ReactNode;
  onOpenEntity?: () => void;
  tooltipTitle?: React.ReactNode | string;
}

const useStyles = makeStyles()(() => ({
  paper: {
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
  },
}));

function CustomPopper(props: PopperProps) {
  const styles = {
    popper: {
      width: 'fit-content',
    },
  };
  return <Popper {...props} style={styles.popper} placement="bottom-start" />;
}

export function LookupAutocomplete<DataType>(props: ILookupAutocompleteProps<DataType>) {
  const {
    className,
    required,
    onChange,
    onSearchValueChange,
    onClose,
    error,
    errorText,
    noOptionsText,
    variant = 'outlined',
    fetchMinimumInputLength = 2,
    throttleMs = 400,
    helperText,
    autoFocus = false,
    label = 'Label',
    placeholder = 'Fremsøg',
    data = [],
    isOptionEqualToValue,
    value,
    getOptionLabel,
    renderOption,
    isLoading = false,
    onOpenEntity,
    tooltipTitle,
    ...rest
  } = props;

  const { classes } = useStyles();
  const [selectedValue, setSelectedValue] = useState<DataType | undefined>(value);
  const [searchValue, setSearchValue] = useState('');

  function handleOnChange(event: ChangeEvent<unknown>, selected: DataType) {
    setSelectedValue(selected || ({} as DataType));
    onChange && onChange(selected);
  }

  const onSearchValueChangeRef = useRef(onSearchValueChange);
  onSearchValueChangeRef.current = onSearchValueChange;
  const debouncedSetValue = useMemo(() => debounce((input: string) => onSearchValueChangeRef.current?.(input), throttleMs), [throttleMs]);

  function handleInputChange(event: React.SyntheticEvent, input: string, reason: string) {
    if (reason !== 'input' && reason !== 'clear') return;
    setSearchValue(input);
    debouncedSetValue.cancel();
    if (!input || throttleMs === 0) onSearchValueChangeRef.current?.(input);
    else debouncedSetValue(input);
  }

  function handleClose() {
    debouncedSetValue.cancel();
    if (onClose) {
      setSearchValue('');
      onClose();
    }
  }

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  useEffect(() => () => debouncedSetValue.cancel(), [debouncedSetValue]);

  return (
    <Box sx={{ position: 'relative' }}>
      <LookupLoadingIndicator loading={isLoading} />
      <Autocomplete<DataType | undefined, false, true>
        {...rest}
        options={data}
        isOptionEqualToValue={isOptionEqualToValue}
        onChange={handleOnChange as never}
        onInputChange={handleInputChange}
        onClose={handleClose}
        classes={{
          paper: classes.paper,
        }}
        className={className ?? ''}
        disableListWrap
        autoHighlight
        selectOnFocus
        value={containsValue(selectedValue) ? selectedValue : (null as never)} // null instead of undefined, to avoid warnings about switching between controlled and uncontrolled
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        renderInput={params => (
          <InnerTextField
            params={params}
            autoFocus={autoFocus}
            error={error}
            errorText={errorText}
            helperText={helperText}
            label={label}
            onOpenEntity={onOpenEntity}
            placeholder={placeholder}
            required={required}
            variant={variant}
            tooltipTitle={tooltipTitle}
          />
        )}
        noOptionsText={noOptionsText ?? (searchValue.length >= fetchMinimumInputLength ? 'Ingen resultater fundet' : 'Skriv for at søge')}
        loadingText="Søger..."
        loading={isLoading}
        PopperComponent={CustomPopper}
        filterOptions={options => options}
      />
    </Box>
  );
}

interface IInnerTextFieldProps {
  params: AutocompleteRenderInputParams;

  placeholder?: TextFieldProps['placeholder'];
  onOpenEntity?: () => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  errorText?: FilledTextFieldProps['helperText'];
  helperText?: FilledTextFieldProps['helperText'];
  variant?: TextFieldProps['variant'];
  autoFocus?: TextFieldProps['autoFocus'];

  tooltipTitle?: React.ReactNode | string;
}

function InnerTextField(props: IInnerTextFieldProps) {
  const { params, placeholder, onOpenEntity, error, errorText, helperText, label, required, variant, autoFocus, tooltipTitle } = props;
  const { InputProps, ...restParams } = params;

  const textField = (
    <TextField
      {...restParams}
      InputProps={{
        ...InputProps,
        placeholder,
        startAdornment: onOpenEntity ? (
          <Box sx={{ padding: '6px' }}>
            <FontAwesomeIcon cursor="pointer" icon={faArrowUpRightFromSquare} onClick={onOpenEntity} />
          </Box>
        ) : undefined,
      }}
      label={label}
      required={required}
      error={error}
      errorText={errorText}
      helperText={helperText}
      variant={variant}
      multiline={false}
      inputProps={{
        ...params.inputProps,
        autoComplete: 'off',
      }}
      autoFocus={autoFocus}
    />
  );

  return tooltipTitle ? (
    <Tooltip title={tooltipTitle}>
      <span>{textField}</span>
    </Tooltip>
  ) : (
    textField
  );
}
